import { apiClient } from "../client";
import { ApiErrorHandler } from "../utils/error-handler";
import { logger } from "../../../utils/logger";
import { Message, ChatMessage, ChatSession } from "../../../types/chat";
import { PaginatedResponse } from "../types/common";
import { 
  ChatSessionCreateRequest, 
  ChatSessionListResponse, 
  ChatMessageCreateRequest, 
  ChatMessageListResponse,
  ChatSessionCloseRequest,
  GeneratedDoc,
  GeneratedDocListResponse
} from "../types/chat";

/**
 * Endpoints de chat
 * 
 * Funcionalidades:
 * - Gerenciamento de sessões de chat
 * - Envio e recebimento de mensagens
 * - Controle de sessões ativas
 * - Integração com WebSocket
 */

export interface CreateSessionRequest {
  document_id: string;
  status?: string;
}

export interface SendMessageRequest {
  session: string;
  message: string;
  sender: string;
}

export const chatApi = {
  // Session management
  /**
   * Busca uma sessão de chat pelo ID
   * 
   * @param sessionId - ID da sessão
   * @returns Sessão encontrada
   */
  getSession: async (sessionId: string): Promise<ChatSession> => {
    try {
      const response = await apiClient.get(`/chat/sessions/${sessionId}/`);
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ChatAPI.getSession");
    }
  },

  /**
   * Lista sessões de chat por documento
   * 
   * @param documentId - ID do documento
   * @returns Lista de sessões
   */
  listSessions: async (
    documentId: string,
  ): Promise<PaginatedResponse<ChatSession>> => {
    try {
      const response = await apiClient.get("/chat/sessions/", {
        params: { document_id: documentId },
      });
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ChatAPI.listSessions");
    }
  },

  /**
   * Cria uma nova sessão de chat
   * 
   * @param documentId - ID do documento
   * @returns Sessão criada
   */
  createSession: async (documentId: string): Promise<ChatSession> => {
    try {
      logger.debug("[ChatAPI] Creating session for document:", documentId);

      // SEMPRE verificar primeiro se já existe uma sessão ativa
      const activeSessions = await chatApi.getActiveSessions(documentId);
      if (activeSessions.length > 0) {
        logger.debug(
          "[ChatAPI] Reusing existing active session:",
          activeSessions[0].id,
        );
        return activeSessions[0];
      }

      // Se não existe sessão ativa, criar uma nova
      logger.debug("[ChatAPI] No active session found, creating new one");
      const payload: CreateSessionRequest = {
        document_id: documentId,
        status: "active",
      };
      const response = await apiClient.post("/chat/sessions/", payload);
      if (!response.data?.id) {
        throw new Error("No session ID returned from server");
      }

      logger.debug("[ChatAPI] New session created:", response.data.id);
      return response.data;
    } catch (error) {
      logger.error("[ChatAPI] Error creating session:", error);

      // Se o erro for de constraint violation (sessão já existe), tentar buscar novamente
      if (ApiErrorHandler.isConstraintViolation(error)) {
        logger.debug("[ChatAPI] Session already exists, fetching it");
        const activeSessions = await chatApi.getActiveSessions(documentId);
        if (activeSessions.length > 0) {
          return activeSessions[0];
        }
      }

      ApiErrorHandler.handleRequestError(error, "ChatAPI.createSession");
    }
  },

  /**
   * Fecha uma sessão de chat
   * 
   * @param sessionId - ID da sessão
   * @param documentId - ID do documento
   */
  closeSession: async (
    sessionId: string,
    documentId: string,
  ): Promise<void> => {
    try {
      logger.debug("[ChatAPI] Closing session:", sessionId);
      await apiClient.post(`/chat/sessions/${sessionId}/close/`, {
        document_id: documentId,
      });
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ChatAPI.closeSession");
    }
  },

  // Message management
  /**
   * Busca mensagens de uma sessão
   * 
   * @param sessionId - ID da sessão
   * @param documentId - ID do documento
   * @returns Lista de mensagens
   */
  getMessages: async (
    sessionId: string,
    documentId: string,
  ): Promise<PaginatedResponse<Message>> => {
    try {
      const response = await apiClient.get(`/chat/sessions/${sessionId}/messages/`, {
        params: { document_id: documentId },
      });
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ChatAPI.getMessages");
    }
  },

  /**
   * Alias para compatibilidade
   */
  getSessionMessages: async (
    sessionId: string,
    documentId: string,
  ): Promise<PaginatedResponse<Message>> => {
    return chatApi.getMessages(sessionId, documentId);
  },

  /**
   * Envia uma mensagem para a sessão
   * 
   * @param sessionId - ID da sessão
   * @param content - Conteúdo da mensagem
   * @returns Mensagem enviada
   */
  sendMessage: async (sessionId: string, content: string): Promise<Message> => {
    try {
      logger.debug("[ChatAPI] Sending message to session:", sessionId);
      const payload: SendMessageRequest = {
        session: sessionId,
        message: content,
        sender: "user",
      };
      const response = await apiClient.post(`/chat/messages/`, payload);
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ChatAPI.sendMessage");
    }
  },

  /**
   * Busca uma mensagem específica
   * 
   * @param sessionId - ID da sessão
   * @param messageId - ID da mensagem
   * @returns Mensagem encontrada
   */
  getMessage: async (
    sessionId: string,
    messageId: string,
  ): Promise<Message> => {
    try {
      const response = await apiClient.get(
        `/chat/sessions/${sessionId}/messages/${messageId}/`,
      );
      return response.data;
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ChatAPI.getMessage");
    }
  },

  /**
   * Busca sessões ativas de um documento
   * 
   * @param documentId - ID do documento
   * @returns Lista de sessões ativas
   */
  getActiveSessions: async (documentId: string): Promise<ChatSession[]> => {
    try {
      logger.debug(
        "[ChatAPI] Fetching active sessions for document:",
        documentId,
      );
      const response = await apiClient.get("/chat/sessions/", {
        params: { document_id: documentId, active: true },
      });
      return response.data.results || [];
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ChatAPI.getActiveSessions");
    }
  },

  /**
   * Garante que existe uma sessão ativa para o documento
   * 
   * @param documentId - ID do documento
   * @returns Sessão ativa
   */
  ensureActiveSession: async (documentId: string): Promise<ChatSession> => {
    try {
      logger.debug(
        "[ChatAPI] Ensuring active session for document:",
        documentId,
      );

      // 1. Verificar se já existe uma sessão ativa
      const activeSessions = await chatApi.getActiveSessions(documentId);
      if (activeSessions.length > 0) {
        logger.debug(
          "[ChatAPI] Found existing active session:",
          activeSessions[0].id,
        );
        return activeSessions[0];
      }

      // 2. Se não existe, criar uma nova
      logger.debug("[ChatAPI] No active session found, creating new one");
      return await chatApi.createSession(documentId);
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ChatAPI.ensureActiveSession");
    }
  },

  /**
   * Fecha uma sessão apenas quando necessário
   * 
   * @param sessionId - ID da sessão
   * @param documentId - ID do documento
   * @param force - Forçar fechamento
   */
  closeSessionIfNeeded: async (
    sessionId: string,
    documentId: string,
    force: boolean = false,
  ): Promise<void> => {
    try {
      if (!force) {
        // Verificar se há outras sessões ativas para o mesmo documento
        const activeSessions = await chatApi.getActiveSessions(documentId);
        const currentSession = activeSessions.find((s) => s.id === sessionId);

        if (!currentSession) {
          logger.debug(
            "[ChatAPI] Session not found or not active, skipping close",
          );
          return;
        }

        // Se é a única sessão ativa, não fechar automaticamente
        if (activeSessions.length === 1) {
          logger.debug(
            "[ChatAPI] Only active session, not closing automatically",
          );
          return;
        }
      }

      // Fechar a sessão
      await chatApi.closeSession(sessionId, documentId);
      logger.debug("[ChatAPI] Session closed:", sessionId);
    } catch (error) {
      ApiErrorHandler.handleRequestError(error, "ChatAPI.closeSessionIfNeeded");
    }
  },
};

/**
 * Endpoints de documentos gerados
 */
export const generatedDocApi = {
  /**
   * Lista todos os documentos gerados do usuário
   */
  list: async (): Promise<GeneratedDoc[]> => {
    try {
      const response = await apiClient.get("/generated-docs/");
      return response.data;
    } catch (error) {
      console.error("[generatedDocApi] Error fetching generated docs:", error);
      throw error;
    }
  },

  /**
   * Busca um documento gerado pelo ID
   */
  get: async (id: string): Promise<GeneratedDoc> => {
    try {
      const response = await apiClient.get(`/generated-docs/${id}/`);
      return response.data;
    } catch (error) {
      console.error("[generatedDocApi] Error fetching generated doc:", error);
      throw error;
    }
  },

  /**
   * Download do documento DOCX gerado
   */
  download: async (id: string): Promise<Blob> => {
    try {
      const response = await apiClient.get(`/generated-docs/${id}/download/`, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("[generatedDocApi] Error downloading generated doc:", error);
      throw error;
    }
  },

  /**
   * Download da versão markdown do documento gerado
   */
  downloadMarkdown: async (id: string): Promise<Blob> => {
    try {
      const response = await apiClient.get(`/generated-docs/${id}/download_markdown/`, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      console.error("[generatedDocApi] Error downloading markdown:", error);
      throw error;
    }
  },

  /**
   * Busca documentos gerados por mensagem de chat
   */
  getByChatMessage: async (chatMessageId: string): Promise<GeneratedDocListResponse> => {
    try {
      const response = await apiClient.get(`/generated-docs/?chat_message=${chatMessageId}`);
      return response.data;
    } catch (error) {
      console.error("[generatedDocApi] Error fetching docs by chat message:", error);
      throw error;
    }
  },
}; 